import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Send, FileSignature } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ESignatureModal, type ESignaturePayload } from '@/components/esignature/ESignatureModal';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

// TODO(rmp): Part-specific schema-driven form. For MVP we show raw JSON per Part.

const PARTS: { key: string; label: string }[] = [
  { key: 'partI', label: 'Part I - Product overview' },
  { key: 'partII', label: 'Part II - Safety specification' },
  { key: 'partIII', label: 'Part III - Pharmacovigilance plan' },
  { key: 'partIV', label: 'Part IV - Plans for post-auth efficacy' },
  { key: 'partV', label: 'Part V - Risk minimisation measures' },
  { key: 'partVI', label: 'Part VI - Summary of activities' },
  { key: 'partVII', label: 'Part VII - Annexes' },
];

interface RmpDetail {
  id: string;
  product: string | null;
  activeSubstance: string | null;
  version: string | null;
  status: string | null;
  updatedAt: string;
  content: Record<string, unknown> | null;
}

export function RmpDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const canApprove = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];

  const [activePart, setActivePart] = useState(PARTS[0].key);
  const [draft, setDraft] = useState<string>('');
  const [esignOpen, setEsignOpen] = useState(false);

  const rmp = useQuery({
    queryKey: ['rmp', id],
    queryFn: async () => {
      try {
        return (await api.get(`/api/rmp/${id}`)).data as RmpDetail;
      } catch {
        return null;
      }
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(draft);
      } catch {
        throw new Error('Invalid JSON');
      }
      return api.patch(`/api/rmp/${id}`, { part: activePart, content: parsed });
    },
    onSuccess: () => {
      toast.success('Saved', `${activePart} updated`);
      qc.invalidateQueries({ queryKey: ['rmp', id] });
    },
    onError: (err) => toast.error('Save failed', extractApiError(err).message),
  });

  const submit = useMutation({
    mutationFn: async () => api.post(`/api/rmp/${id}/submit`, {}),
    onSuccess: () => {
      toast.success('Submitted for review');
      qc.invalidateQueries({ queryKey: ['rmp', id] });
    },
    onError: (err) => toast.error('Submit failed', extractApiError(err).message),
  });

  const approve = useMutation({
    mutationFn: async (sig: ESignaturePayload) =>
      api.post(`/api/rmp/${id}/approve`, {
        esignature: {
          password: sig.password,
          reason: `${sig.reason}: ${sig.intent}`,
        },
      }),
    onSuccess: () => {
      toast.success('RMP approved', 'E-signature recorded');
      setEsignOpen(false);
      qc.invalidateQueries({ queryKey: ['rmp', id] });
    },
    onError: (err) => toast.error('Approval failed', extractApiError(err).message),
  });

  if (rmp.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!rmp.data) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/rmp">
            <ArrowLeft className="mr-2 h-4 w-4" /> RMPs
          </Link>
        </Button>
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            RMP not found or backend endpoint not yet available.
          </CardContent>
        </Card>
      </div>
    );
  }

  const r = rmp.data;
  const content = (r.content ?? {}) as Record<string, unknown>;
  const activeValue = content[activePart];
  const activeText = activeValue != null ? JSON.stringify(activeValue, null, 2) : '{}';

  function onSelectPart(k: string) {
    setActivePart(k);
    const v = content[k];
    setDraft(v != null ? JSON.stringify(v, null, 2) : '{}');
  }

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/rmp">
            <ArrowLeft className="mr-2 h-4 w-4" /> RMPs
          </Link>
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{r.product ?? 'RMP'}</h1>
            <p className="text-sm text-muted-foreground">
              {r.activeSubstance ?? '-'} - v{r.version ?? '-'} - updated {formatDate(r.updatedAt)}
            </p>
          </div>
          <Badge variant={r.status === 'APPROVED' ? 'success' : 'warning'}>
            {r.status ?? 'DRAFT'}
          </Badge>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {PARTS.map((p) => (
          <button
            key={p.key}
            onClick={() => onSelectPart(p.key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              activePart === p.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{PARTS.find((p) => p.key === activePart)?.label}</CardTitle>
          <CardDescription>
            Edit the JSON payload for this part. TODO: schema-driven form.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={18}
            className="font-mono text-xs"
            value={draft || activeText}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="{}"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save.mutate()} disabled={!canEdit || save.isPending}>
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {save.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => submit.mutate()}
              disabled={!canEdit || submit.isPending || r.status === 'SUBMITTED'}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {submit.isPending ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setEsignOpen(true)}
              disabled={!canApprove || r.status === 'APPROVED'}
            >
              <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Approve (e-sign)
            </Button>
          </div>
        </CardContent>
      </Card>

      <ESignatureModal
        open={esignOpen}
        onOpenChange={setEsignOpen}
        title="Approve RMP draft"
        description={`Approving ${r.product ?? 'RMP'} (v${r.version ?? '-'}). This locks the current content for authority submission.`}
        reasonPresets={[
          { value: 'approval', label: 'Approval' },
          { value: 'authorisation', label: 'Authorisation' },
        ]}
        submitLabel="Sign and approve"
        isSubmitting={approve.isPending}
        onConfirm={async (sig) => {
          await approve.mutateAsync(sig);
        }}
      />
    </div>
  );
}
